import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Printer, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { printSelections } from "@/components/admin/printSelections";

const ACTIONS = [
  ["acceptée", "Accepter", "bg-emerald-500 text-[var(--ink)] hover:bg-emerald-400"],
  ["en cours", "Mettre en attente", "border border-[var(--line)] text-[var(--parchment)] hover:border-[var(--gold)]"],
  ["refusée", "Refuser", "border border-red-400 text-red-300 hover:bg-red-500/10"],
];
const STATUS_CLS = { nouvelle: "text-[var(--gold-soft)]", "en cours": "text-[var(--parchment)]", "acceptée": "text-emerald-400", "refusée": "text-red-300" };
const field = "h-10 w-full rounded-sm border border-[var(--line)] bg-transparent px-3 text-sm outline-none focus:border-[var(--gold)]";

const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const NOTIF = { interne: "Notification espace", email: "Email" };
const NOTIF_CLS = { "envoyé": "text-emerald-400", disponible: "text-emerald-400", "échec": "text-red-300", "non configuré": "text-[var(--gold-soft)]" };

function History({ list }) {
  if (!list.length) return null;
  return (
    <details className="mt-2">
      <summary className="cursor-pointer text-[var(--gold-soft)] underline">Historique des notifications ({list.length})</summary>
      <ul className="mt-2 space-y-1 text-xs">
        {list.map((n, i) => <li key={`${n.at}-${n.channel}-${i}`}>{fmt(n.at)} · {NOTIF[n.channel] || n.channel} · <span className={NOTIF_CLS[n.status]}>{n.status}</span> · {n.trigger}{n.detail ? ` · ${n.detail}` : ""}</li>)}
      </ul>
    </details>
  );
}

function InviteInfo({ a, onChanged }) {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try { await api.post(`/admin/ambassadors/${a.id}/resend-mission`); toast.success(`Mission envoyée à ${a.email}`); } catch (e) { toast.error(errMsg(e)); }
    onChanged();
    setBusy(false);
  };
  const m = a.mission;
  const sent = a.invite_status === "envoyé" ? <span className="text-emerald-400">envoyé le {fmt(a.invite_sent_at)}</span>
    : a.invite_status === "échec" ? <span className="text-red-300">échec de l'envoi le {fmt(a.invite_sent_at)} — cliquez sur « Renvoyer »</span>
    : a.invite_status === "non configuré" ? <span className="text-[var(--gold-soft)]">email non configuré : mission visible uniquement dans son espace</span>
    : <span className="text-[var(--parchment)]/80">aucun envoi enregistré</span>;
  return (
    <div data-testid={`admin-amb-invite-${a.id}`} className="mt-3 space-y-1 rounded-sm border border-[var(--line)] p-4 text-sm">
      <p>Code promo : <b className="text-[var(--gold-soft)]">{a.promo_code || "—"}</b></p>
      {a.accepted_at ? <p data-testid={`admin-amb-accepted-at-${a.id}`}>Acceptée le : {fmt(a.accepted_at)}</p> : null}
      <p data-testid={`admin-amb-mission-${a.id}`}>Première mission : {m ? <span className={m.status === "terminée" ? "text-emerald-400" : "text-[var(--gold-soft)]"}>créée le {fmt(m.created_at)} · {m.status} ({m.done}/{m.total})</span> : <span className="text-red-300">pas encore créée — cliquez sur « Envoyer la mission »</span>}</p>
      {m?.comments ? <p className="text-[var(--parchment)]/85">Ses commentaires : « {m.comments} »</p> : null}
      <p data-testid={`admin-amb-invite-status-${a.id}`}>Email de mission : {sent}</p>
      <p data-testid={`admin-amb-password-${a.id}`}>Mot de passe créé : {a.has_password ? <span className="text-emerald-400">oui, son espace est activé</span> : <span className="text-[var(--gold-soft)]">pas encore</span>}</p>
      <History list={Array.isArray(a.notifications) ? a.notifications : []} />
      <button onClick={resend} disabled={busy} data-testid={`admin-amb-resend-${a.id}`}
        className="mt-2 h-10 rounded-full border border-[var(--gold)] px-5 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--gold-soft)] transition-opacity disabled:opacity-50">{busy ? "Envoi…" : m ? "Renvoyer la mission" : "Envoyer la mission"}</button>
    </div>
  );
}

function Application({ a, onChanged }) {
  const [f, setF] = useState({ promo_code: a.promo_code, note: a.note });
  const save = async (status = a.status) => {
    if (status === "refusée" && !window.confirm(`Refuser ${a.first_name} ? Son code et son accès seront désactivés.`)) return;
    try {
      const r = await api.patch(`/admin/ambassadors/${a.id}`, { ...f, status });
      if (r.data?.invite_status === "échec" && status === "acceptée") toast.error("Candidature acceptée, mais l'email de mission n'est pas parti. Cliquez sur « Renvoyer la mission ».");
      else toast.success(`Candidature : ${status}`);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Supprimer définitivement la candidature de ${a.first_name} ${a.last_name} ?`)) return;
    await api.delete(`/admin/ambassadors/${a.id}`); onChanged();
  };
  return (
    <li data-testid={`admin-amb-${a.id}`} className="rounded-sm border border-[var(--line)] p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-display text-xl">{a.first_name} {a.last_name} <span className="text-sm text-[var(--parchment)]/70">· {a.created_at.slice(0, 10)}</span></p>
          <p className="mt-1 text-sm text-[var(--parchment)]/85">{a.email} · {a.network} · {a.handle}</p>
          <a href={a.profile_url} target="_blank" rel="noopener noreferrer nofollow" className="mt-1 inline-flex items-center gap-1 text-sm text-[var(--gold-soft)] underline">{a.profile_url} <ExternalLink className="h-3 w-3" /></a>
          {a.other_links ? <p className="mt-1 text-sm text-[var(--parchment)]/80">Autres : {a.other_links}</p> : null}
        </div>
        <button onClick={remove} data-testid={`admin-amb-delete-${a.id}`} title="Supprimer (RGPD)" className="rounded-full border border-[var(--line)] p-2 hover:border-red-400"><Trash2 className="h-4 w-4" /></button>
      </div>
      <p className="mt-3 text-sm text-[var(--parchment)]/90">« {a.motivation} »</p>
      <p data-testid={`admin-amb-current-status-${a.id}`} className="mt-4 text-sm">Statut actuel : <b className={STATUS_CLS[a.status]}>{a.status}</b></p>
      {a.status === "acceptée" ? <InviteInfo a={a} onChanged={onChanged} /> : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {ACTIONS.map(([s, label, cls]) => (
          <button key={s} onClick={() => save(s)} disabled={a.status === s} data-testid={`admin-amb-${s === "acceptée" ? "accept" : s === "refusée" ? "refuse" : "pending"}-${a.id}`}
            className={`h-10 rounded-full px-5 text-xs font-semibold uppercase tracking-[0.16em] transition-opacity disabled:opacity-40 ${cls}`}>{label}</button>
        ))}
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-[180px_1fr_auto]">
        <input placeholder="Code promo personnel" value={f.promo_code} onChange={(e) => setF({ ...f, promo_code: e.target.value })} data-testid={`admin-amb-code-${a.id}`} className={field} />
        <input placeholder="Note interne" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid={`admin-amb-note-${a.id}`} className={field} />
        <button onClick={() => save()} data-testid={`admin-amb-save-${a.id}`} className="h-10 rounded-full border border-[var(--gold)] px-5 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--gold-soft)]">Enregistrer code / note</button>
      </div>
    </li>
  );
}

function ProgramSettings() {
  const { data } = useQuery({ queryKey: ["amb-program"], queryFn: () => api.get("/ambassadors/program").then((r) => r.data) });
  const [f, setF] = useState(null);
  const qc = useQueryClient();
  useEffect(() => { if (data && !f) setF(data); }, [data, f]);
  if (!f) return null;
  const save = async () => {
    try { await api.put("/admin/ambassadors/program", f); qc.invalidateQueries({ queryKey: ["amb-program"] }); toast.success("Programme mis à jour"); } catch (e) { toast.error(errMsg(e)); }
  };
  const area = (k, label, rows = 3) => (
    <label className="block text-sm"><span className="mb-1 block text-xs text-[var(--parchment)]/80">{label}</span>
      <textarea rows={rows} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`admin-amb-program-${k}`} className={`${field} h-auto py-2`} /></label>
  );
  return (
    <section data-testid="admin-amb-program" className="mb-10 space-y-4 rounded-sm border border-[var(--line)] p-6">
      <h3 className="font-display text-2xl">Réglages du programme (affichés sur la page)</h3>
      <label className="block text-sm"><span className="mb-1 block text-xs text-[var(--parchment)]/80">Exemple de code affiché</span>
        <input value={f.example_code} onChange={(e) => setF({ ...f, example_code: e.target.value.toUpperCase() })} data-testid="admin-amb-program-example_code" className={`${field} max-w-xs`} /></label>
      {area("conditions", "Conditions du programme (section « Conditions » de la page)", 6)}
      {area("missions_rule", "FAQ : rythme de publication attendu")}
      {area("missed_rule", "FAQ : si les missions ne sont pas réalisées")}
      <button onClick={save} data-testid="admin-amb-program-save" className="h-10 rounded-full bg-[var(--gold)] px-6 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ink)]">Enregistrer les réglages</button>
    </section>
  );
}

function Selections() {
  const qc = useQueryClient();
  const month = new Date().toISOString().slice(0, 7);
  const { data } = useQuery({ queryKey: ["admin-amb-sel", month], queryFn: () => api.get("/admin/ambassador-selections", { params: { month } }).then((r) => r.data) });
  const list = Array.isArray(data) ? data : [];
  const { data: prods } = useQuery({ queryKey: ["admin-product-names"], queryFn: () => api.get("/products").then((r) => r.data) });
  const { data: apps } = useQuery({ queryKey: ["admin-amb"], queryFn: () => api.get("/admin/ambassadors").then((r) => r.data) });
  const names = Object.fromEntries((Array.isArray(prods) ? prods : []).map((p) => [p.slug, p.name]));
  const codes = Object.fromEntries((Array.isArray(apps) ? apps : []).map((a) => [a.email, a.promo_code]));
  const print = (rows) => { if (!printSelections(rows, names, codes)) toast.error("Autorisez les fenêtres pop-up pour imprimer."); };
  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/ambassador-selections/${id}`, { status }); qc.invalidateQueries({ queryKey: ["admin-amb-sel"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section data-testid="admin-amb-selections" className="mb-10 rounded-sm border border-[var(--line)] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-2xl">Produits offerts du mois ({month}) · {list.length}</h3>
        {list.length ? <button onClick={() => print(list)} data-testid="admin-amb-sel-print-all" className="inline-flex h-10 items-center gap-2 rounded-full bg-[var(--gold)] px-5 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ink)]"><Printer className="h-4 w-4" />Imprimer toutes les fiches</button> : null}
      </div>
      {list.length === 0 ? <p className="mt-3 text-sm text-[var(--parchment)]/80">Aucune sélection pour ce mois.</p> : (
        <ul className="mt-4 space-y-3">{list.map((s) => (
          <li key={s.id} data-testid={`admin-amb-sel-${s.id}`} className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--line)] pb-3 text-sm">
            <div><b>{s.ambassador_name}</b> · {s.products.map((p) => names[p] || p).join(" + ")}<br /><span className="text-[var(--parchment)]/80">{s.relay ? `Mondial Relay ${s.relay.id} · ${s.relay.name}, ` : ""}{s.name}, {s.address}, {s.zip} {s.city} · {s.phone}{s.contact_email ? ` · ${s.contact_email}` : ""}</span></div>
            <div className="flex items-center gap-2">
              <button onClick={() => print([s])} data-testid={`admin-amb-sel-print-${s.id}`} title="Imprimer la fiche" className="inline-flex h-10 items-center gap-2 rounded-full border border-[var(--gold)] px-4 text-xs font-semibold text-[var(--gold-soft)]"><Printer className="h-4 w-4" />Imprimer la fiche</button>
              <select value={s.status} onChange={(e) => setStatus(s.id, e.target.value)} data-testid={`admin-amb-sel-status-${s.id}`} className={`${field} !w-40 bg-[var(--ink)]`}>{["à valider", "validée", "expédiée"].map((x) => <option key={x}>{x}</option>)}</select>
            </div>
          </li>))}</ul>
      )}
    </section>
  );
}

function Missions() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-amb-posts"], queryFn: () => api.get("/admin/ambassador-posts").then((r) => r.data) });
  const groups = Array.isArray(data?.ambassadors) ? data.ambassadors : [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-amb-posts"] });
  const run = async (fn) => { try { await fn(); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section data-testid="admin-amb-missions" className="mb-10 rounded-sm border border-[var(--line)] p-6">
      <h3 className="font-display text-2xl">Publications et missions du mois ({data?.month || ""})</h3>
      {groups.length === 0 ? <p className="mt-3 text-sm text-[var(--parchment)]/80">Aucun lien déposé ce mois-ci.</p> : groups.map((g) => (
        <div key={g.ambassador_id} data-testid={`admin-amb-missions-${g.ambassador_id}`} className="mt-5 border-t border-[var(--line)] pt-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-semibold">{g.ambassador_name} · {g.posts.filter((p) => p.kind === "post").length} posts · {g.posts.filter((p) => p.kind === "story").length} stories</p>
            <label className="flex items-center gap-2 text-sm">Mission
              <select value={g.mission_status} onChange={(e) => run(() => api.put("/admin/ambassador-missions", { ambassador_id: g.ambassador_id, month: data.month, status: e.target.value }))}
                data-testid={`admin-amb-mission-status-${g.ambassador_id}`} className={`${field} !w-40 bg-[var(--ink)]`}>{["en cours", "validée", "non réalisée"].map((x) => <option key={x}>{x}</option>)}</select></label>
          </div>
          <ul className="mt-3 space-y-2">{g.posts.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <span className="min-w-0"><b className="uppercase">{p.kind}</b> <a href={p.url} target="_blank" rel="noopener noreferrer nofollow" className="break-all text-[var(--gold-soft)] underline">{p.url}</a>{p.note ? ` · ${p.note}` : ""}</span>
              <select value={p.status} onChange={(e) => run(() => api.patch(`/admin/ambassador-posts/${p.id}`, { status: e.target.value }))} data-testid={`admin-amb-post-status-${p.id}`} className={`${field} !w-36 bg-[var(--ink)]`}>{["à vérifier", "validée", "refusée"].map((x) => <option key={x}>{x}</option>)}</select>
            </li>))}</ul>
        </div>
      ))}
    </section>
  );
}

export function AmbassadorsAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-amb"], queryFn: () => api.get("/admin/ambassadors").then((r) => r.data) });
  const list = Array.isArray(data) ? data : [];
  return (
    <div data-testid="admin-ambassadors">
      <ProgramSettings />
      <Selections />
      <Missions />
      <p className="mb-4 text-sm text-[var(--parchment)]/80">Cliquer sur « Accepter » crée automatiquement son code -20 % (prénom + 20 si le champ est vide), sa première mission (une seule fois par candidature) et lui envoie l'email de bienvenue avec la mission et le lien pour créer son mot de passe. « Refuser » désactive le code et l'accès (aucun email de refus n'est envoyé).</p>
      <h3 className="mb-4 font-display text-2xl">Candidatures ({list.length})</h3>
      {list.length === 0 ? <p data-testid="admin-amb-empty" className="text-sm text-[var(--parchment)]/80">Aucune candidature pour l'instant.</p> : (
        <ul className="space-y-4">{list.map((a) => <Application key={a.id} a={a} onChanged={() => qc.invalidateQueries({ queryKey: ["admin-amb"] })} />)}</ul>
      )}
    </div>
  );
}
